import { Editor, Element, Frame } from '@craftjs/core';
import { Table } from '@lmt/unicorn-shared/app/components/editor/components/blocks/Table';
import clsx from 'clsx';
import React, { FC, useImperativeHandle } from 'react';
import { Loading } from '../common/Loading';
import { Container } from './components/blocks/Container';
import { EmptyBlock } from './components/blocks/EmptyBlock';
import { Input } from './components/blocks/Input';
import { Text } from './components/blocks/Text';
import { Title } from './components/blocks/Title';
import { Toolbox } from './components/Toolbox';
import {
  FieldBlockConfiguration,
  InputValuesContext,
  ToolbarComponentType,
  UIEditorData,
  UIEditorDataObjectValue,
} from './UIEditorTypes';

import styles from './UIEditor.module.scss';
import { DataBlockTypeDto, DataBlockWithValuesDto } from '@lmt/shared-types';

type UIEditorProps = {
  // are inputs currently read-only or not (note that Designer/nonDesigner view is controlled by `isInDesignerMode`).
  isOnlyView?: boolean;
  // required for localizing field titles
  // should include the Variant type, like 'patient.information' or 'measurement.spirometry'
  dataBlockTypeNameForLocalization: string;
  dataBlockType: DataBlockTypeDto | undefined;
  dataBlock?: DataBlockWithValuesDto;
  config?: string;
  /*
  If configKey changes the UIEditor contents will be recreated.
  It's needed to support the following scenario:
  1. Summary and FullView are the same
  2. Open Summary in DataBlockConfig, change something on UI.
  3. Switch to FullView.
  4. Expected: config for FullView is shown (same as initial Summary)
  5. Actual: changed config stays the same (because props.config isn't change, unchanged FullView and Summary are the same)
  */
  configKey?: string;
  inputValues?: UIEditorData;
  changeValues?: (key: string, value: UIEditorDataObjectValue) => void;
  inputErrors?: { [key: string]: string };
  changeErrors?: (key: string, error: string) => void;
  sharedValues?: UIEditorData;
  changeSharedValues?: (key: string, value: UIEditorDataObjectValue) => void;
  showErrors?: boolean;
} & (
  | {
      // whether Configuration (moving blocks around/adding new blocks) is enabled or not
      isInDesignerMode: true;
      /*
Component that is rendered above the editor Frame.
It's rendered within Editor, so it has access to `useEditor` hook and could control the Editor.
*/
      toolbarComponent: ToolbarComponentType;
      fieldBlocks: FieldBlockConfiguration[];
      setIsLoading: (isLoading: boolean) => void;
      setConfigKey: (configKey: string) => void;
    }
  | {
      // whether Configuration (moving blocks around/adding new blocks) is enabled or not
      isInDesignerMode: false;
    }
);

const UIEditor: FC<UIEditorProps> = React.memo(function UIEditor(props) {
  const AdditionalControls = props.isInDesignerMode
    ? props.toolbarComponent
    : null;

  return (
    <div className={styles.container}>
      <InputValuesContext.Provider
        value={{
          isOnlyView: props.isOnlyView ?? false,
          dataBlockTypeName: props.dataBlockTypeNameForLocalization,
          dataBlockType: props.dataBlockType,
          dataBlock: props.dataBlock,
          values: props.inputValues,
          setValues: props.changeValues,
          errors: props.inputErrors,
          setErrors: props.changeErrors,
          sharedValues: props.sharedValues,
          setSharedValues: props.changeSharedValues,
          showErrors: props.showErrors ?? false,
        }}
      >
        <Editor
          resolver={{ Input, Text, Container, Title, EmptyBlock, Table }}
          enabled={props.isInDesignerMode}
        >
          {/*{!props.isInDesignerMode && <DisableDesignMode />}*/}
          {AdditionalControls && props.isInDesignerMode && (
            <AdditionalControls
              setIsLoading={props.setIsLoading}
              setConfigKey={props.setConfigKey}
            />
          )}
          {/*When UI Editor is in non-editable mode we have to update configs if they have changed*/}
          {/*{!props.isInDesignerMode && <ConfigUpdater config={props.config} />}*/}
          {!(props.isInDesignerMode && !props.config) && (
            <Loading
              loading={props.config === undefined}
              wrapperClassName={
                props.isInDesignerMode ? 'overflowAuto' : 'overflowHidden'
              }
            >
              <div
                className={clsx(
                  styles.editorContainer,
                  props.isInDesignerMode && styles.editorDesigner,
                )}
              >
                <div
                  className={styles.editor}
                  data-test-id={'ui-editor-container'}
                >
                  <Frame
                    data={props.config}
                    key={props.configKey ?? props.config}
                  >
                    <Element canvas={true} id="rootCanvas" is={Container} />
                  </Frame>
                </div>

                {props.isInDesignerMode && (
                  <div className={styles.toolbox}>
                    <Toolbox
                      fieldBlocks={props.fieldBlocks}
                      dataBlockTypeName={props.dataBlockTypeNameForLocalization}
                    />
                  </div>
                )}
              </div>
            </Loading>
          )}
        </Editor>
      </InputValuesContext.Provider>
    </div>
  );
});

export default UIEditor;
